import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { map, Observable, of } from 'rxjs';
import { signal } from '@angular/core';
import { Tag, OutputTag, InternalTag } from '@shared/components/tags/model';
import { MiqTagsModule, TagsComponent } from '@shared/components/tags';
import { LookupRefService } from '@shared/services/lookup-ref.service';

@Component({
  selector: 'miq-tag-editor-wrapper',
  template: `
    <div class="tags-editing-box-wrapper">
      <miq-tags
        #tagsComponent
        [tags]="tags"
        [canEdit]="canEdit"
        [enableClipboardFeatures]="true"
        [filterStreamFn]="filterTags"
        [addTag]="addTagFn"
        [entityId]="entityId"
        [deleteTag]="deleteTagFn"
        [addTagWhenEnter]="addTagWhenEnter"
        (clipboardCut)="onClipboardCut($event)"
        (clipboardPaste)="onClipboardPaste($event)"
        (chipClick)="onChipClick($event.tag, $event.event)"
      ></miq-tags>
    </div>
  `,
  styleUrls: ['./tag-editor-wrapper.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MiqTagsModule],
})
export class TagEditorWrapperComponent implements OnInit {
  @ViewChild('tagsComponent') tagsComponent: TagsComponent;

  @Input() tags: Tag[] = [];
  @Input() canEdit = true;
  @Input() filterUrl: string;
  @Input() entityId: number | string;
  @Input() addTagWhenEnter = false;

  @Output() tagsChanged = new EventEmitter<Tag[]>();

  public enableClipboardFeatures = true;

  /**
   * Signal for tag state management
   */
  private tagsSignal = signal<Tag[]>([]);

  /**
   * Next ID generator for newly created tags
   */
  private nextTagId = signal<number>(1);

  /**
   * Bound callbacks for miq-tags component
   */
  public filterTags: (filter: string) => Observable<string[]>;
  public addTagFn: (tag: OutputTag) => Observable<number>;
  public deleteTagFn: (tag: OutputTag) => Observable<boolean>;

  constructor(
    private cdr: ChangeDetectorRef,
    private lookupRefService: LookupRefService
  ) {
    this.filterTags = (filter: string) => this.onFilterTags(filter);
    this.addTagFn = (tag: OutputTag) => this.onAddTag(tag);
    this.deleteTagFn = (tag: OutputTag) => this.onDeleteTag(tag);
  }

  ngOnInit(): void {
    if (!this.tags) {
      this.tags = [];
    }
    this.tagsSignal.set(this.tags);
  }

  /**
   * Filter tags by query
   */
  private onFilterTags(query: string): Observable<string[]> {
    return this.lookupRefService
      .getTagsByLookup(this.filterUrl, query)
      .pipe(map((tags: { key: string; value: string }[]) => tags.map(t => t.value)));
  }

  /**
   * Handle tag addition
   * Returns the assigned ID for the new tag (required by miq-tags)
   */
  private onAddTag(tag: OutputTag): Observable<number> {
    const newTag: Tag = {
      id: tag.id || this.nextTagId(),
      name: tag.name,
    } as Tag;

    // Update signal
    this.tagsSignal.update(tags => [...tags, newTag]);

    // Emit changes (tracking happens in cell editor's destroy())
    this.tagsChanged.emit(this.tagsSignal());

    // Increment next ID for future tags
    if (!tag.id) {
      this.nextTagId.update(id => id + 1);
      return of(newTag.id as number);
    }

    return of(newTag.id as number);
  }

  /**
   * Handle tag deletion
   * Returns true on success, false for rollback
   */
  private onDeleteTag(tag: OutputTag): Observable<boolean> {
    this.tagsSignal.update(tags => tags.filter(t => !(t.name === tag.name)));
    // Tracking happens in cell editor's destroy()
    this.tagsChanged.emit(this.tagsSignal());
    return of(true);
  }

  /**
   * Handle chip click event - toggle tag selection
   */
  public onChipClick(tag: InternalTag, event: Event): void {
    if (!this.canEdit || !this.enableClipboardFeatures) return;

    const viewTagsComponent = this.tagsComponent?.viewTagsComponent;
    if (!viewTagsComponent) return;

    event.stopPropagation();
    const tagId = tag.internalId;

    // Toggle selection state in ViewTagsComponent
    if (viewTagsComponent.selectedTagIds.has(tagId)) {
      viewTagsComponent.selectedTagIds.delete(tagId);
    } else {
      viewTagsComponent.selectedTagIds.add(tagId);
    }

    // Set clipboard support flag
    if (this.enableClipboardFeatures) {
      viewTagsComponent.isClipboardSupported = true;
    }

    // Trigger change detection
    this.cdr.markForCheck();
  }

  /**
   * Handle clipboard cut event from directive
   * Tracks cut tags as removed for the entity
   */
  public onClipboardCut(_event: { tagNames: string[] }): void {
    // Get current tags and selected IDs from the ViewTagsComponent
    const viewTagsComponent = this.tagsComponent?.viewTagsComponent;
    if (!viewTagsComponent) return;

    const currentTagList = this.tagsComponent.currentTagList || [];
    const selectedTagIds = viewTagsComponent.selectedTagIds;
    const tagIdsToRemove = new Set(selectedTagIds);
    const cutTags = currentTagList.filter(tag => tagIdsToRemove.has(tag.internalId));

    // Update local state - filter by tag names that were cut
    const cutTagNames = new Set(cutTags.map(t => t.name));
    this.tagsSignal.update(tags => tags.filter(tag => !cutTagNames.has(tag.name)));

    // Clear selection in ViewTagsComponent
    viewTagsComponent.selectedTagIds.clear();
    this.tagsChanged.emit(this.tagsSignal());
  }

  /**
   * Handle clipboard paste event from directive
   */
  public onClipboardPaste(event: { clipboardTags: string[] }): void {
    // Filter out tags that already exist
    const existingTagNames = new Set(this.tagsSignal().map(t => t.name));
    const newTagNames = event.clipboardTags.filter(name => !existingTagNames.has(name));

    // Add each new clipboard tag
    newTagNames.forEach(tagName => {
      const newTag: Tag = {
        id: this.nextTagId(),
        name: tagName,
      } as Tag;

      // Update signal
      this.tagsSignal.update(tags => [...tags, newTag]);

      // Increment next ID
      this.nextTagId.update(id => id + 1);
    });

    // Emit changes
    this.tagsChanged.emit(this.tagsSignal());
  }
}
